import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { APIENDPOINT, API_URLS } from '@core/api-url.enum';
import { ApiCallService } from '@services/api-call.service';
import { CommonService } from '@modules/shared/services/common.service';
import { PurchasePointsModalComponent } from '@modules/wallet/component/loyalty-points/purchase-points-modal/purchase-points-modal.component';
import {
  getLoyaltyPointsPaymentData,
  LOYALTY_POINTS_TEXT
} from '@modules/wallet/component/loyalty-points/loyalty-points.constants';
import type {
  LoyaltyPointsBundleId,
  LoyaltyPointsPackage,
  LoyaltyPointsPaymentUser,
  PurchasePointsResponse
} from '@modules/wallet/component/loyalty-points/loyalty-points.interface';
import { PAYMENT_FOR_TYPE, PAYMENT_REDIRECTS } from '@constants/constant';
import { Application } from '@enums/application.enums';
import type { Button } from '@modules/dynamic-form-builder/model/formBuilderInterface';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { EMPTY, Subject, switchMap, takeUntil } from 'rxjs';
import { UNLOCK_WITH_POINTS_MODAL_TEXT } from '@components/unlock-with-points-modal/unlock-with-points-modal.constants';
import type {
  CreateLoyaltyPaymentOrderFn,
  LoyaltyPaymentOrderResponse
} from '@components/unlock-with-points-modal/unlock-with-points-modal.interface';
import { ApiCacheService, CacheTypes } from '@core/cache/api-cache.service';
import { POST_PRICE_LOYALTY } from '@modules/dashboard/components/new-post/newposts/constants';

@Component({
  selector: 'app-unlock-with-points-modal',
  templateUrl: './unlock-with-points-modal.component.html',
  styleUrls: ['./unlock-with-points-modal.component.scss']
})
export class UnlockWithPointsModalComponent implements OnInit, OnDestroy {
  @ViewChild(PurchasePointsModalComponent)
  purchasePointsModal?: PurchasePointsModalComponent;

  readonly TEXT = UNLOCK_WITH_POINTS_MODAL_TEXT;
  readonly LOYALTY_POINTS_TEXT = LOYALTY_POINTS_TEXT;
  readonly coinIconSrc = POST_PRICE_LOYALTY.POINT_COIN_ICON_PATH;

  headerTitle = '';
  contentTitle = '';
  contentDescription?: string;
  pointsCost = 0;
  creatorId = '';
  fanId = '';
  createLoyaltyPaymentOrder?: CreateLoyaltyPaymentOrderFn;
  onUnlockSuccess: () => void = () => {};
  creatorUserForPayment: LoyaltyPointsPaymentUser | null = null;
  loyaltyPointsPurchaseReturnUrl = '';
  isPortfolioPointsPurchase = false;
  onBeforeLoyaltyPointsPurchaseNavigate?: () => void;

  currentBalance: number | null = null;
  balanceLoading = true;
  unlockSubmitting = false;
  purchasePointsSubmitting = false;
  selectedBundleId: LoyaltyPointsBundleId | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly cs: CommonService,
    private readonly apiCallService: ApiCallService,
    private readonly router: Router,
    public readonly bsModalRef: BsModalRef,
    private readonly apiCacheService: ApiCacheService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pointsShortfall(): number {
    if (this.currentBalance == null) {
      return Math.max(Application.ZERO, this.pointsCost);
    }
    return Math.max(Application.ZERO, this.pointsCost - this.currentBalance);
  }

  get hasEnoughPoints(): boolean {
    if (this.currentBalance == null) {
      return false;
    }
    return this.currentBalance >= this.pointsCost;
  }

  get primaryUnlockButton(): Button {
    return {
      type: 'button',
      name: UNLOCK_WITH_POINTS_MODAL_TEXT.PRIMARY_BTN_NAME_UNLOCK,
      class: 'btn btn-primary',
      label:
        UNLOCK_WITH_POINTS_MODAL_TEXT.UNLOCK_WITH_PREFIX +
        this.pointsCost.toLocaleString() +
        UNLOCK_WITH_POINTS_MODAL_TEXT.UNLOCK_WITH_SUFFIX,
      emClass: 'icon-litelock-filled-white-new1',
      loading: this.unlockSubmitting,
      disabled: this.unlockSubmitting || this.balanceLoading
    };
  }

  get primaryGetMoreButton(): Button {
    return {
      type: 'button',
      name: UNLOCK_WITH_POINTS_MODAL_TEXT.PRIMARY_BTN_NAME_GET_MORE,
      class: 'btn btn-primary',
      label:
        UNLOCK_WITH_POINTS_MODAL_TEXT.GET_MORE_CTA_PREFIX +
        this.pointsShortfall.toLocaleString() +
        UNLOCK_WITH_POINTS_MODAL_TEXT.GET_MORE_CTA_MIDDLE,
      emClass: 'icon-litelock-filled-white-new1',
      loading: this.purchasePointsSubmitting,
      disabled: this.purchasePointsSubmitting || this.balanceLoading
    };
  }

  ngOnInit(): void {
    if (!this.cs.isBrowser) {
      return;
    }
    this.loadBalance();
  }

  close(): void {
    this.bsModalRef.hide();
  }

  loadBalance(): void {
    if (!this.creatorId || !this.fanId) {
      this.balanceLoading = false;
      return;
    }
    this.balanceLoading = true;
    this.apiCacheService
      .get(APIENDPOINT.fanPoints(this.creatorId, this.fanId), CacheTypes.ALWAYS_FRESH)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { status?: boolean; data?: { current_points_balance?: number } }) => {
          if (res?.status && res.data) {
            this.currentBalance = Number(res.data.current_points_balance ?? Application.ZERO);
          } else {
            this.currentBalance = Application.ZERO;
          }
          this.balanceLoading = false;
        },
        error: () => {
          this.currentBalance = Application.ZERO;
          this.balanceLoading = false;
        }
      });
  }

  onPrimaryClick(name?: string): void {
    if (name === UNLOCK_WITH_POINTS_MODAL_TEXT.PRIMARY_BTN_NAME_GET_MORE) {
      this.purchasePointsModal?.openModal();
      return;
    }
    if (name === UNLOCK_WITH_POINTS_MODAL_TEXT.PRIMARY_BTN_NAME_UNLOCK) {
      this.runUnlockFlow();
    }
  }

  private runUnlockFlow(): void {
    if (this.unlockSubmitting) {
      return;
    }
    if (!this.createLoyaltyPaymentOrder) {
      this.cs.showErrorMessage(UNLOCK_WITH_POINTS_MODAL_TEXT.UNABLE_TO_START_UNLOCK);
      return;
    }
    this.unlockSubmitting = true;
    this.createLoyaltyPaymentOrder()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((res: LoyaltyPaymentOrderResponse) => {
          if (!res?.status || !res.order_sk) {
            this.unlockSubmitting = false;
            this.cs.showWarningMessage(res?.message || UNLOCK_WITH_POINTS_MODAL_TEXT.UNABLE_TO_START_UNLOCK);
            return EMPTY;
          }
          return this.apiCallService.post(
            API_URLS.v2Payment,
            {
              pay_using_wallet: false,
              orderSK: res.order_sk,
              couponCode: null
            },
            false
          );
        })
      )
      .subscribe({
        next: (payRes: { status?: boolean; message?: string }) => {
          if (payRes.status) {
            this.cs.showSuccessMessage(payRes.message);
            this.onUnlockSuccess();
            this.bsModalRef.hide();
          } else if (payRes) {
            this.cs.showWarningMessage(payRes.message);
          }
          this.unlockSubmitting = false;
        },
        error: (err: Error) => {
          this.cs.showErrorMessage(err?.message);
          this.unlockSubmitting = false;
        }
      });
  }

  handlePackageSelected(loyaltyPointsPackage: LoyaltyPointsPackage): void {
    if (this.purchasePointsSubmitting || !this.creatorUserForPayment) {
      if (!this.creatorUserForPayment) {
        this.cs.showErrorMessage(UNLOCK_WITH_POINTS_MODAL_TEXT.UNABLE_TO_START_CHECKOUT);
      }
      return;
    }
    this.selectedBundleId = loyaltyPointsPackage.bundle_id;
    this.purchasePointsSubmitting = true;
    this.apiCallService
      .post(
        API_URLS.purchasePoints,
        {
          bundle_id: loyaltyPointsPackage.bundle_id,
          creator_id: this.creatorId
        },
        true
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PurchasePointsResponse) => {
          if (response.status) {
            this.purchasePointsModal?.closeModal();
            if (this.isPortfolioPointsPurchase) {
              this.onBeforeLoyaltyPointsPurchaseNavigate?.();
            }
            this.cs.subscriptionPaymentData.next(
              getLoyaltyPointsPaymentData(
                response.data?.order_sk,
                loyaltyPointsPackage,
                this.creatorUserForPayment,
                PAYMENT_FOR_TYPE.LOYALTY_POINTS
              )
            );
            this.router.navigate([Application.MAKE_PAYMENT], {
              queryParams: {
                redirect: PAYMENT_REDIRECTS.LOYALTY_POINTS_PURCHASE_RETURN,
                url: this.loyaltyPointsPurchaseReturnUrl
              }
            });
            this.bsModalRef.hide();
          }
          this.purchasePointsSubmitting = false;
          this.selectedBundleId = null;
        },
        error: (err: Error) => {
          this.cs.showErrorMessage(err.message);
          this.purchasePointsSubmitting = false;
          this.selectedBundleId = null;
        }
      });
  }
}
